import { useState, useEffect } from "react";
// Bootstrap components
import { Container, Row, Col, Card, Form, Button, Spinner } from "react-bootstrap";
// Font Awesome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
// Custom components Google and Microsoft login buttons
import GoogleButton from "../../components/GoogleButton/GoogleButton"
import MicrosoftButton from "../../components/MicrosoftButton/MicrosoftButton"
import { useLogin } from "../../hooks/useLogin";
// Toast notifications 
import { toast } from 'react-toastify';
// Navigation
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/img/Icone_Niralakka_blanc.svg";
import loginBG from "../../assets/img/loginBG.png";

// Login component that handles user authentication
const Login = () => {
  const navigate = useNavigate(); // Navigation hook
  const location = useLocation(); // Location hook to get redirect destination
  const loginMutation = useLogin(); // Custom hook for login mutation

  const [email, setEmail] = useState(""); // State for email input
  const [password, setPassword] = useState(""); // State for password input
  const [showPassword, setShowPassword] = useState(false); // State for password visibility
  const [isDark, setIsDark] = useState(false); // State for dark mode detection

  useEffect(() => {
    // Detect initial dark mode
    const isDarkMode = document.documentElement.getAttribute('data-bs-theme') === 'dark';
    setIsDark(isDarkMode);

    // Listen for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-bs-theme') {
          setIsDark(document.documentElement.getAttribute('data-bs-theme') === 'dark');
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  // Get the page user was trying to access before being redirected to login
  const from = location.state?.from?.pathname || '/home';
  
 
  // Handle form submission for login
  const handleSubmit = (e) => {
    e.preventDefault();

    const loginPromise = loginMutation.mutateAsync({ email, password }); // Trigger login mutation
    
    // Show toast notifications based on login promise status
    toast.promise(
      loginPromise.then(() => {
        navigate(from, { replace: true });// Navigate to original destination or home on successful login
      }),
      {
        pending: 'Connexion en cours...',
        success: 'Bienvenue !',
        error: 'Échec de la connexion',
      }
    );
  };


  // JSX for rendering the login form
  return (
    <>
      <div className="login-bg-blur" style={{ backgroundImage: `url(${loginBG})` }}></div>

      <div className="login-overlay" style={{ minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", zIndex: 1 }}>

      {/* Centered container for the login card*/ }
      <Container style={{ padding: '1rem' }}>
        <Row className="justify-content-center">
          <Col xl={5} lg={6} md={8} sm={10} xs={12}>
            <Card className=" border-0" style={{ 
              overflow: "hidden", 
              borderRadius: "2rem",
              background: isDark ? "rgba(30, 30, 30, 0.6)" : "rgba(255, 255, 255, 0.3)",
              backdropFilter: "blur(50px)",
            }}>
              <div className="d-flex align-items-center justify-content-center" style={{
                minHeight: "100px",
                paddingTop: "15px",
                paddingBottom: "10px"
              }}>
                <img src={logo} alt="Logo" style={{ maxHeight: "80px", maxWidth: "90%", marginTop: "30px"}} />
              </div>

              <Row className="g-0">
                {/* Login Form Section */}
                <Col lg={12}>
                  <Card.Body className="login-card-body" style={{ minHeight: "auto", display: "flex", flexDirection: "column", justifyContent: "flex-start", paddingTop: "1.5rem" }}>
                    {/* Title */}
                    <div className="text-center mb-2">
                      <h1 className="login-welcome-title" style={{ color: 'white' }}>Bienvenue !</h1>
                    </div>
                    <div className={`text-center ${isDark ? 'login-text-muted' : 'login-text-muted-light'}`} style={{ marginBottom: "20px", color: !isDark ? 'white' : undefined }}>
                      <h1 className="h5">Connectez vous à Niralakka :</h1>
                    </div>
                    {/* Login Form */}
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3" controlId="formEmail">
                        <Form.Label className={`mb-2 ${isDark ? 'login-text-muted' : 'login-text-muted-light'}`} style={{ color: !isDark ? 'white' : undefined }}><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '8px' }} />E-mail</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Entrez votre adresse e-mail..."
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          style={{ 
                            borderRadius: "20px", 
                            padding: "15px", 
                            background: "rgba(255, 255, 255, 0.2)",
                            border: `1px solid ${isDark ? 'var(--bs-gray-500)' : 'var(--bs-gray-300)'}`,
                            color: "white"
                          }}
                          className="placeholder-white"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3" controlId="formPassword">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label className={`mb-0 ${isDark ? 'login-text-muted' : 'login-text-muted-light'}`} style={{ color: !isDark ? 'white' : undefined }}><FontAwesomeIcon icon={faLock} style={{ marginRight: '8px' }} />Mot de passe</Form.Label>
                          <Button
                            variant="link"
                            onClick={() => navigate('/forgot-password')}
                            style={{
                              color: 'white',
                              textDecoration: 'none',
                              fontSize: '12px',
                              padding: '0'
                            }}
                            className="p-0"
                          >
                            Mot de passe oublié ?
                          </Button>
                        </div>
                        <div style={{ position: 'relative' }}>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Mot de passe"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            style={{ 
                              borderRadius: "20px", 
                              padding: "15px", 
                              paddingRight: "45px",
                              background: "rgba(255, 255, 255, 0.2)",
                              border: `1px solid ${isDark ? 'var(--bs-gray-500)' : 'var(--bs-gray-300)'}`,
                              color: "white",
                              marginBottom: "30px",
                            }}
                            className="placeholder-white"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: 'absolute',
                              right: '15px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              color: `${isDark ? 'var(--bs-gray-400)' : 'var(--bs-gray-300)'}`,
                              cursor: 'pointer',
                              padding: '0',
                              width: '30px',
                              height: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '16px',
                              lineHeight: '1'
                            }}
                          >
                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                          </button>
                        </div>
                      </Form.Group>
                      {/* Button submit */}
                      <Button
                        type="submit"
                        variant="primary"
                        className="w-75 mx-auto d-block btn-primary"
                        disabled={loginMutation.isPending}
                        style={{ borderRadius: "1rem", padding: "10px" }}
                      >
                       {/* Show spinner when login is pending */}
                        {loginMutation.isPending ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />{" "}
                            Connexion...
                          </>
                        ) : (
                          "Se connecter"
                        )}
                      </Button>

                      {/* Display error message if login fails */}
                      {loginMutation.isError && (
                        <div className="text-danger text-center mt-2">
                          {loginMutation.error?.message || "Erreur lors de la connexion"}
                        </div>
                      )}
                    </Form>
                    <div className="position-relative my-3" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <hr style={{ flex: 1, margin: 0, borderColor: 'white' }} />
                      <span style={{ color: isDark ? '#999' : 'white', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>ou</span>
                      <hr style={{ flex: 1, margin: 0, borderColor: 'white' }} />
                    </div>
                  {/* Google login buttons */}
                    <div className="d-flex justify-content-center mb-2">
                      <div style={{ width: '350px' }}>
                        <GoogleButton />
                      </div>
                    </div>
                  {/* Microsoft login button */}
                    <div className="d-flex justify-content-center">
                      <div style={{ width: '350px' }}>
                        <MicrosoftButton />
                      </div>
                    </div>
                  </Card.Body>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>
      </div>
    </>
  );
};

export default Login;
